import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Download, Loader, AlertCircle, Grid, FileText } from 'lucide-react';

const ExcelViewerFormatted = ({ url, fileName }) => {
  const [workbook, setWorkbook] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState(0);
  const [sheetHtml, setSheetHtml] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (url) {
      loadExcelFile();
    }
  }, [url]);

  const loadExcelFile = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch Excel file: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      
      // Read with all formatting options enabled
      const wb = XLSX.read(arrayBuffer, {
        type: 'array',
        cellStyles: true,  // Read cell styles
        cellHTML: true,    // Generate HTML for cells
        cellDates: true,   // Parse dates
        cellFormula: true, // Include formulas
        cellNF: true,      // Include number formats
        sheetStubs: true,  // Include empty cells
        sheetRows: 0,      // Read all rows
        raw: false,        // Format values
        dense: false       // Use sparse mode
      });

      setWorkbook(wb);
      
      // Generate HTML for the first sheet
      if (wb.SheetNames.length > 0) {
        generateSheetHtml(wb, 0);
      }

      setIsLoading(false);
    } catch (err) {
      console.error('Error loading Excel file:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  const generateSheetHtml = (wb, sheetIndex) => {
    try {
      const sheetName = wb.SheetNames[sheetIndex];
      const worksheet = wb.Sheets[sheetName];
      
      // Convert to HTML with styling
      const html = XLSX.utils.sheet_to_html(worksheet, {
        header: `
          <style>
            table { 
              border-collapse: collapse; 
              width: 100%; 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              font-size: 13px;
            }
            th, td { 
              border: 1px solid #d1d5db; 
              padding: 6px 12px; 
              text-align: left;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 300px;
            }
            th { 
              background-color: #f3f4f6; 
              font-weight: 600;
              color: #374151;
              position: sticky;
              top: 0;
              z-index: 10;
            }
            tr:hover { 
              background-color: #f9fafb; 
            }
            td.number { 
              text-align: right; 
              font-variant-numeric: tabular-nums;
            }
            td.date { 
              font-variant-numeric: tabular-nums;
            }
            td.formula { 
              background-color: #fef3c7; 
            }
            td.header { 
              font-weight: 600;
              background-color: #eff6ff;
            }
            .cell-comment {
              position: relative;
              cursor: help;
            }
            .cell-comment::after {
              content: '📝';
              position: absolute;
              top: 0;
              right: 0;
              font-size: 10px;
            }
            /* Custom cell colors from Excel */
            .bg-yellow { background-color: #fef3c7; }
            .bg-green { background-color: #d1fae5; }
            .bg-blue { background-color: #dbeafe; }
            .bg-red { background-color: #fee2e2; }
            .bg-gray { background-color: #f3f4f6; }
            .text-bold { font-weight: 600; }
            .text-italic { font-style: italic; }
            .text-underline { text-decoration: underline; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
          </style>
        `,
        footer: ''
      });

      // Post-process HTML to add better formatting
      const processedHtml = postProcessHtml(html, worksheet);
      setSheetHtml(processedHtml);
      setSelectedSheet(sheetIndex);

    } catch (err) {
      console.error('Error generating sheet HTML:', err);
      setSheetHtml('<p>Error rendering sheet</p>');
    }
  };

  const postProcessHtml = (html, worksheet) => {
    // Add additional formatting based on cell properties
    let processedHtml = html;

    // Get cell styles if available
    if (worksheet['!cols']) {
      // Add column width styles
      const colStyles = worksheet['!cols'].map((col, idx) => {
        if (col && col.wpx) {
          return `td:nth-child(${idx + 1}) { width: ${col.wpx}px; }`;
        }
        return '';
      }).filter(s => s).join('\n');

      if (colStyles) {
        processedHtml = processedHtml.replace('</style>', `${colStyles}\n</style>`);
      }
    }

    // Add row heights if available
    if (worksheet['!rows']) {
      const rowStyles = worksheet['!rows'].map((row, idx) => {
        if (row && row.hpx) {
          return `tr:nth-child(${idx + 1}) { height: ${row.hpx}px; }`;
        }
        return '';
      }).filter(s => s).join('\n');

      if (rowStyles) {
        processedHtml = processedHtml.replace('</style>', `${rowStyles}\n</style>`);
      }
    }

    // Handle merged cells
    if (worksheet['!merges']) {
      const mergeStyles = worksheet['!merges'].map((merge, idx) => {
        const colspan = merge.e.c - merge.s.c + 1;
        const rowspan = merge.e.r - merge.s.r + 1;
        return `
          .merge-${idx} {
            grid-column: span ${colspan};
            grid-row: span ${rowspan};
          }
        `;
      }).join('\n');

      if (mergeStyles) {
        processedHtml = processedHtml.replace('</style>', `${mergeStyles}\n</style>`);
      }
    }

    return processedHtml;
  };

  const handleSheetChange = (index) => {
    if (workbook) {
      generateSheetHtml(workbook, index);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'excel-file.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAsHtml = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${fileName || 'Excel Export'}</title>
      </head>
      <body>
        ${sheetHtml}
      </body>
      </html>
    `;
    
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName || 'excel'}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8">
        <Loader className="h-8 w-8 animate-spin text-blue-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Loading Excel File...</h3>
        <p className="text-sm text-gray-600 mt-2">Preserving formatting and styles</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Unable to Load Excel File</h3>
        <p className="text-sm text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleDownload}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <Download className="h-4 w-4 mr-2" />
          Download Original
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Toolbar */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Sheet tabs */}
            {workbook && workbook.SheetNames.length > 1 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Sheet:</span>
                <div className="flex space-x-1">
                  {workbook.SheetNames.map((name, index) => (
                    <button
                      key={index}
                      onClick={() => handleSheetChange(index)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                        selectedSheet === index
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={exportAsHtml}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
            >
              <FileText className="h-3 w-3 mr-1" />
              Export HTML
            </button>
            <button
              onClick={handleDownload}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
            >
              <Download className="h-3 w-3 mr-1" />
              Download
            </button>
          </div>
        </div>
      </div>

      {/* Excel content with preserved formatting */}
      <div className="flex-grow relative overflow-hidden">
        <div className="absolute inset-0 overflow-auto overscroll-contain p-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div 
            dangerouslySetInnerHTML={{ __html: sheetHtml }}
            className="excel-formatted-content"
          />
        </div>
      </div>

      {/* Status bar */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-2">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Formatted view with preserved styles</span>
          <span>{fileName}</span>
        </div>
      </div>
    </div>
  );
};

export default ExcelViewerFormatted;